import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ListOrdered, Plus } from 'lucide-react';
import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from 'react';
import { Button } from '@/components/atoms/button';
import { FormSectionCard, FormSectionHeader } from '@/components/molecules';
import { OptionCard, type OptionState } from '@/components/organisms/OptionCard';
import {
  computeProposalOptionPreview,
  resolveOptionMembershipInfo,
} from '@/routes/_dashboard/dashboard/proposals/_shared';
import type { EstimateMembershipInfo } from '@/hooks/estimates/useEstimateMembershipInfo';
import type { MembershipPlan } from '@/components/organisms/pricebook/support/membership-plan-shared';
import { useEstimateSettings } from '@/hooks/settings/useEstimateSettings';
import { ProposalOptionPaymentSchedule } from './ProposalOptionPaymentSchedule';
import { proposalOptionDepositAmountOn } from './support/proposal-option-math';

interface ProposalOptionsSectionProps {
  options: OptionState[];
  maxOptions: number;
  onAddOption: () => void;
  onUpdateOption: (index: number, updates: Partial<OptionState>) => void;
  onDuplicateOption: (index: number) => void;
  onRemoveOption: (index: number) => void;
  onReorderOptions: (options: OptionState[]) => void;
  // NOT-605 — the customer's ACTIVE membership (applies to every option), plus the
  // plans catalog used to resolve a per-option in-cart membership (join-&-save).
  // Each option's context is resolved individually so an option that sells a plan
  // prices its own siblings at member rates while the others stay at list.
  activeMembershipInfo?: EstimateMembershipInfo | null;
  membershipPlansById?: Map<string, MembershipPlan>;
  membershipCustomerId?: string | null;
  membershipServiceAddressId?: string | null;
  /**
   * The Business Unit chosen for this proposal in the create flow. Narrows the
   * estimate templates each option can import lines from; undefined = no filter.
   */
  businessUnitId?: string;
}

function optionDomId(index: number) {
  return `proposal-option-${index}`;
}

export function ProposalOptionsSection({
  options,
  maxOptions,
  onAddOption,
  onUpdateOption,
  onDuplicateOption,
  onRemoveOption,
  onReorderOptions,
  activeMembershipInfo = null,
  membershipPlansById,
  membershipCustomerId,
  membershipServiceAddressId,
  businessUnitId,
}: ProposalOptionsSectionProps) {
  const { data: estimateSettings } = useEstimateSettings();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  // Member-aware totals so the header amount + tier delta match each card. Each
  // option resolves its own membership context (active member, or a plan sold in
  // that option) so the join-&-save savings show only where a plan is included.
  const plansById = membershipPlansById ?? new Map();
  const optionMembership = (option: OptionState) =>
    resolveOptionMembershipInfo(option, activeMembershipInfo, plansById);
  const totals = options.map((option) =>
    computeProposalOptionPreview(option, optionMembership(option)),
  );
  const hasRecommended = options.some((entry) => entry.recommended);
  const canAddOption = options.length < maxOptions;

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = options.findIndex((option) => option.clientId === active.id);
    const newIndex = options.findIndex((option) => option.clientId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorderOptions(arrayMove(options, oldIndex, newIndex));
  }

  return (
    <FormSectionCard className="overflow-hidden rounded-lg border bg-card p-0 shadow-none">
      <div className="border-b px-4 py-3">
        <FormSectionHeader
          icon={ListOrdered}
          title="Options"
          description="Compare tiers and build the customer-facing proposal options."
          extra={
            canAddOption ? (
              <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onAddOption}>
                <Plus size={13} className="mr-1" />
                Add option
              </Button>
            ) : null
          }
        />
      </div>

      <div className="space-y-4 px-4 py-4">
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={options.map((option) => option.clientId)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {options.map((option, index) => {
                const total = totals[index]?.total ?? 0;
                const previousTotal = totals[index - 1]?.total ?? 0;
                return (
                  <SortableOptionCard
                    key={option.clientId}
                    id={option.clientId}
                    domId={optionDomId(index)}
                    opt={option}
                    idx={index}
                    totalOptions={options.length}
                    hasRecommended={hasRecommended}
                    delta={index > 0 ? total - previousTotal : undefined}
                    previousLabel={
                      index > 0 ? options[index - 1]?.label || `Option ${index}` : undefined
                    }
                    canDuplicate={canAddOption}
                    membershipInfo={optionMembership(option)}
                    membershipCustomerId={membershipCustomerId}
                    membershipServiceAddressId={membershipServiceAddressId}
                    businessUnitId={businessUnitId}
                    paymentScheduleControls={
                      <ProposalOptionPaymentSchedule
                        value={option.paymentScheduleTemplate ?? null}
                        contactId={membershipCustomerId ?? null}
                        grandTotal={total}
                        depositRequired={option.depositRequired}
                        depositAmount={proposalOptionDepositAmountOn(
                          {
                            depositType: option.depositType === '%' ? 'PERCENT' : 'FLAT',
                            depositValue: option.depositValue,
                          },
                          totals[index]?.depositEligibleTotal ?? total,
                        )}
                        allowPaymentSchedules={
                          estimateSettings?.paymentSchedule?.allowPaymentSchedules !== false
                        }
                        requireScheduleAboveAmount={
                          estimateSettings?.paymentSchedule?.requireScheduleAboveAmount ?? 0
                        }
                        onChange={(value) =>
                          onUpdateOption(index, { paymentScheduleTemplate: value })
                        }
                      />
                    }
                    onUpdate={(updates) => onUpdateOption(index, updates)}
                    onDuplicate={() => onDuplicateOption(index)}
                    onRemove={() => onRemoveOption(index)}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </FormSectionCard>
  );
}

function SortableOptionCard({
  id,
  domId,
  canDuplicate,
  ...props
}: {
  id: string;
  domId: string;
  opt: OptionState;
  idx: number;
  totalOptions: number;
  hasRecommended: boolean;
  delta?: number;
  previousLabel?: string;
  canDuplicate: boolean;
  paymentScheduleControls?: ReactNode;
  membershipInfo?: EstimateMembershipInfo | null;
  membershipCustomerId?: string | null;
  membershipServiceAddressId?: string | null;
  businessUnitId?: string;
  onUpdate: (updates: Partial<OptionState>) => void;
  onDuplicate: () => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
  };
  const dragHandleProps = {
    ...attributes,
    ...listeners,
  } as ButtonHTMLAttributes<HTMLButtonElement>;

  return (
    <div id={domId} ref={setNodeRef} style={style}>
      <OptionCard
        {...props}
        isDragging={isDragging}
        canDuplicate={canDuplicate}
        dragHandleProps={dragHandleProps}
      />
    </div>
  );
}
